import {Component, Input} from '@angular/core';
import {CommonModule} from '@angular/common';

/** Mirrors WeeklyDirection on patient-weekly-snapshot.model.ts. */
export type WeeklyDirectionValue = 'better' | 'noChange' | 'worse';

interface DirectionPresentation {
    arrow: string;
    text: string;
    colorClass: 'green' | 'grey' | 'yellow';
}

const PRESENTATIONS: Record<WeeklyDirectionValue, DirectionPresentation> = {
    better: {arrow: '↑', text: 'Better', colorClass: 'green'},
    noChange: {arrow: '→', text: 'No change', colorClass: 'grey'},
    worse: {arrow: '↓', text: 'Worse', colorClass: 'yellow'},
};

/** `direction: null` (no comparable week) — deliberately distinct copy from
 *  'noChange' so an absent value is never read as "nothing changed". */
const NO_CHECK_IN: DirectionPresentation = {arrow: '—', text: 'No check-in', colorClass: 'grey'};

/**
 * Shared weekly direction badge (UNIVAPLAT-3034). Renders an arrow + text for
 * the `better` / `noChange` / `worse` observation-area values used across the
 * v2 weekly panels (WeeklyObservationAreaV2.direction).
 *
 * Direction is never conveyed by colour alone: the arrow icon is
 * `aria-hidden`, and the text label is always present in the DOM — visible by
 * default, or `.visually-hidden` when `showText` is set false for a denser
 * layout (same pattern as cdss-daily-behaviour-panel's trend icon, UNIVAPLAT-2360).
 *
 * `direction: null` renders the no-check-in state, never "No change" — those
 * are clinically distinct (no comparable data vs. an observed lack of change).
 */
@Component({
    selector: 'cdss-weekly-direction-badge',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './weekly-direction-badge.component.html',
    styleUrl: './weekly-direction-badge.component.scss'
})
export class WeeklyDirectionBadgeComponent {
    @Input() direction: WeeklyDirectionValue | null = null;

    /** Visible by default — set false to visually hide the text label in a dense
     *  layout while keeping it available to screen readers. */
    @Input() showText = true;

    get presentation(): DirectionPresentation {
        return this.direction ? PRESENTATIONS[this.direction] : NO_CHECK_IN;
    }

    get styleClass(): string {
        return `direction-badge--${this.presentation.colorClass}`;
    }
}
